import { each, isNull, extend, omit } from 'lodash';
import { hasWebAttributionPackage } from '@adjust/dashboard-core/lib/utils/account';
/**
   * @ngdoc directive
   * @name adjust.common.directive:filter
   *
   * @description
   * Filter directive for statistics pages
   *
   */
function filter() {
  'ngInject';
  return {
    restrict: 'E',
    scope: {},
    template: require('./filter.html'),
    controller: 'FilterController',
    controllerAs: 'vm',
    bindToController: {
      accountId: '=',
      loadingState: '=',
      osNames: '=?',
      partners: '=?',
      type: '@',
      screen: '@',
      view: '=?',
      events: '=?',
      eventTypes: '=?',
      flags: '=?',
      onApply: '&',
      trackerToken: '='
    }
  };
}

/**
   * @ngdoc controller
   * @name adjust.common.controller:FilterController
   *
   * @description
   * Controller for the {@link adjust.common.directive:filter filter} directive
   *
   */
function FilterController(
  Filter,
  Account,
  KpiCohorts,
  Helper,
  Column,
  gettextCatalog,
  options,
  $scope,
  $q,
  Moment
) {
  'ngInject';
  var vm = this;

  vm.options = options;
  vm.regions = [];
  vm.countries = [];
  vm.groupings = [];
  vm.timezones = [];
  vm.open = false;
  vm.hasWebAttributionPackage = false;

  vm.apply = apply;
  vm.reset = reset;
  vm.prepareKpiSelection = prepareKpiSelection;
  vm.groupBy = groupBy;
  vm.toggleColumnsSection = toggleColumnsSection;
  vm.translateEvent = Helper.translateEvent;
  vm.onAttributionStatusSelect = onAttributionStatusSelect;
  vm.toggleExcludableParam = toggleExcludableParam;
  vm.isParamExcluded = isParamExcluded;

  $scope.$watch('vm.view', _handleViewChange);
  $scope.$watch('vm.loadingState', _handleChange);
  $scope.$on('rightSidebar:toggle', _handleToggle);

  /**
   * @private
   */
  function _init() {
    return $q.all([
      Account.getCurrent(),
      Moment.timezones()
    ]).then(([account, timezones]) => {
      vm.hasWebAttributionPackage = hasWebAttributionPackage(account);

      if (!vm.regions.length) {
        vm.regions = Filter.getRegions(vm.accountId);
      }

      if (!vm.countries.length) {
        vm.countries = Filter.getCountries();
      }

      if (!vm.groupings.length) {
        vm.groupings = Filter.getGroupings({
          hasWebAttributionPackage: vm.hasWebAttributionPackage,
        });
      }

      if (!vm.timezones.length) {
        vm.timezones = timezones;
      }

      vm.params = Filter.getParams();

      vm.platforms = Filter.getPlatforms();

      if (vm.screen === 'cohorts') {
        vm.bases = Filter.getBases(vm.eventTypes);
        vm.selectedKpi = vm.bases[0].token;
        vm.period = Filter.getPeriod();
      }

      vm.conversionTypeList = Filter.getConversionType();
      vm.attributionSourceList = Filter.getAttributionSource();
      vm.impressionTypeList = Filter.getImpressionType();

      Filter.prepareOsNames(vm.osNames);
      prepareKpiSelection();

      Filter.clearEmptyExcludableParams(vm.params);
    });
  }

  /**
   * @ngdoc method
   * @name _handleViewChange
   * @methodOf adjust.common.controller:FilterController
   *
   * @description
   * Sync view value when changed
   *
   * @param {string} value new view value
   * @private
   */
  function _handleViewChange(value) {

    if (!vm.params) { return; }

    vm.params.view = value;
    vm.groupings = Filter.getGroupings({
      view: value,
      hasWebAttributionPackage: vm.hasWebAttributionPackage
    });

  }

  /**
   * @ngdoc method
   * @name _handleChange
   * @methodOf adjust.common.controller:FilterController
   *
   * @description
   * Handle loading state change callback
   *
   * @param {boolean} loading loading state
   * @private
   */
  function _handleChange(loading) {

    vm.loading = loading;

    if (loading) { return; }

    _init();
  }

  /**
   * @ngdoc method
   * @name _handleToggle
   * @methodOf adjust.common.controller:FilterController
   *
   * @description
   * Subscribe on sidebar toggle and prepare parameters if needed
   *
   */
  function _handleToggle() {

    vm.open = !vm.open;

    if (!vm.open || !vm.params) {
      Filter.clearEmptyExcludableParams(vm.params);
      return;
    }

    vm.showColumnSelection = false;

    prepareKpiSelection();
  }

  /**
   * @ngdoc method
   * @name apply
   * @methodOf adjust.common.controller:FilterController
   *
   * @description
   * Preserve parameters
   *
   */
  function apply() {

    var outerKeys = Filter.getOuterParams();
    var outerParams = {};
    var tmpColumns = Column.getSelected(true);

    each(outerKeys, function(key) {
      outerParams[key] = Filter.getParam(key);
    });

    outerParams.columns = isNull(tmpColumns) ? outerParams.columns : tmpColumns;

    Filter.preserve(extend(vm.params, outerParams));

    if (vm.screen !== 'cohorts' && vm.events) {
      Column.setColumnsVisibility(vm.events, false, vm.params, vm.screen)
        .then(events => {
          vm.events = events;
        });
    }

    vm.onApply();

  }

  /**
   * @ngdoc method
   * @name reset
   * @methodOf adjust.common.controller:FilterController
   *
   * @description
   * Resets parameters and refresh view
   *
   */
  function reset() {

    Filter.resetParams()
      .then(function() {

        vm.flags = omit(vm.flags, 'timezoneDisabled');

        _handleToggle();

        vm.onApply();

      });
  }

  /**
   * @ngdoc method
   * @name prepareKpiSelection
   * @methodOf adjust.common.controller:FilterController
   *
   * @description
   * Prepare selection for the dependant kpi dropdown
   *
   */
  function prepareKpiSelection() {

    if (vm.screen !== 'cohorts') {
      return;
    }

    var attributionSource = vm.params.attribution_source || 'dynamic';

    KpiCohorts.getAllAvailable(attributionSource)
      .then(function(cohorts) {

        vm.selectedKpi = cohorts[vm.params.base] ? vm.params.base : 'events';

      });

  }

  /**
   * @ngdoc method
   * @name groupBy
   * @methodOf adjust.common.controller:FilterController
   *
   * @description
   * Returns parameter name to group by, but translated
   *
   * @param {Object} item item from the list
   * @returns {string} translated group by parameter name
   */
  function groupBy(item) {

    return gettextCatalog.getString(item.group);

  }

  /**
   * @ngdoc method
   * @name toggleColumnsSection
   * @methodOf adjust.common.controller:FilterController
   *
   * @description
   * Toggle columns section visibility
   */
  function toggleColumnsSection() {

    vm.showColumnSelection = !vm.showColumnSelection;

    if (vm.type === 'overview') {
      $scope.$emit('rightSidebar:size', vm.showColumnSelection ? 'bigger' : 'small');
    }

  }

  /**
   * @param {String} value
   */
  function onAttributionStatusSelect(value) {

    if (value === 'false') {
      vm.params.attribution_source = 'dynamic';
    }

  }

  /**
   * @param {Object} $event
   * @param {Boolean} $event.value
   * @param {String} param
   */
  function toggleExcludableParam({value}, param) {

    if (value) {
      Filter.addExcludableParam(param);
      return;
    }

    Filter.removeExcludableParam(param);
  }

  /**
   * @param {String} param
   * @returns {Boolean}
   */
  function isParamExcluded(param) {
    return Filter.isParamExcluded(param);
  }

}

export { FilterController, filter };
